import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Clock, Package, ShoppingCart, History, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionHeader, DLLoading, EmptyState, DLButton, StatTile, Slogan, SimulationDisclaimer } from "@/components/dl/ui";
import { InactiveNotice } from "@/components/dl/EntitlementGate";

export default function DLResources() {
  const { caseId, team } = useOutletContext();
  const [res, setRes] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [confirm, setConfirm] = useState(null); // catalog item pending commit
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!team) return;
    try {
      const [r, c, l] = await Promise.all([
        api.get(`/dl/teams/${team.dl_team_id}/resources`),
        api.get(`/dl/cases/${caseId}/catalog`),
        api.get(`/dl/teams/${team.dl_team_id}/ledger`),
      ]);
      setRes(r.data); setCatalog(c.data || []); setLedger(l.data || []);
    } catch { setRes({ balances: {}, inventory: [] }); }
  }, [team, caseId]);

  useEffect(() => { load(); }, [load]);

  if (!team) return <InactiveNotice />;
  if (res === null) return <DLLoading label="Counting the cap…" />;

  const cap = res.balances?.cap_points?.current ?? 0;
  const hours = res.balances?.attorney_hours?.current ?? 0;
  const afterCap = confirm ? cap - (confirm.cap_cost || 0) : 0;
  const afterHours = confirm ? hours - (confirm.attorney_hour_cost || 0) : 0;
  const cantAfford = confirm && (afterCap < 0 || afterHours < 0);

  const commit = async () => {
    setBusy(true);
    try {
      await api.post(`/dl/teams/${team.dl_team_id}/acquire`, { catalog_resource_id: confirm.catalog_resource_id, reason: reason || `Acquire ${confirm.name}` });
      toast.success(`Committed: ${confirm.name}`);
      setConfirm(null); setReason(""); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <SectionHeader kicker="Resources" title="Manage your resources." desc="Every commitment costs Cap Points and/or Attorney Hours. Spend deliberately — resources are not refunded if a strategy fails." />

      <div className="dl-panel rounded-md border-l-2 border-l-amber-400/60 px-4 py-3" data-testid="dl-resource-disclaimer">
        <p className="text-xs text-white/55">Cap Points and Attorney Hours are simulation resources and do not represent actual defense funding or expenditures.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile testid="dl-res-cap" label="Cap Points Remaining" value={cap} accent />
        <StatTile testid="dl-res-hours" label="Attorney Hours Remaining" value={hours} accent />
        <StatTile testid="dl-res-inv" label="Acquired Resources" value={(res.inventory || []).length} />
        <StatTile testid="dl-res-moves" label="Ledger Entries" value={ledger.length} />
      </div>

      {/* Inventory */}
      <div>
        <SectionHeader kicker="Acquired" title="Your bench" className="mb-3" />
        {(res.inventory || []).length === 0 ? (
          <EmptyState icon={Package} title="Nothing acquired yet" desc="Resources you commit to appear here." testid="dl-inv-empty" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {res.inventory.map((it, i) => (
              <div key={`${it.name}-${it.acquired_at || i}`} className="dl-panel rounded-md p-3 flex items-center gap-3" data-testid="dl-inv-item">
                <Package className="w-4 h-4 text-gold shrink-0" />
                <div><div className="text-white font-medium">{it.name}</div><div className="text-[10px] font-mono text-white/35">{(it.acquired_at || "").slice(0, 10)}</div></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalog */}
      <div>
        <SectionHeader kicker="Resource Catalog" title="Available resources" className="mb-3" />
        {catalog.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="No catalog for this case yet" desc="The Commissioner hasn't published a resource catalog." testid="dl-catalog-empty" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {catalog.map((c) => (
              <div key={c.catalog_resource_id} className="dl-panel rounded-md p-4 flex flex-col" data-testid="dl-catalog-item">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">{c.category}</span>
                  {c.availability && c.availability !== "available" && <span className="text-[10px] font-mono text-red-300 uppercase">{c.availability}</span>}
                </div>
                <div className="font-display font-semibold text-white mt-1">{c.name}</div>
                {c.description && <p className="text-xs text-white/50 mt-1 flex-1">{c.description}</p>}
                <div className="mt-3 flex items-center gap-3 text-sm">
                  {c.cap_cost ? <span className="inline-flex items-center gap-1 text-gold"><Coins className="w-3.5 h-3.5" /> {c.cap_cost} CP</span> : null}
                  {c.attorney_hour_cost ? <span className="inline-flex items-center gap-1 text-white/70"><Clock className="w-3.5 h-3.5" /> {c.attorney_hour_cost} hrs</span> : null}
                </div>
                <div className="mt-3">
                  <DLButton className="w-full" data-testid="dl-acquire-btn" onClick={() => { setConfirm(c); setReason(""); }}><ShoppingCart className="w-4 h-4" /> Commit</DLButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div>
        <SectionHeader kicker="Resource History" title="Every move on the record" className="mb-3" />
        {ledger.length === 0 ? (
          <EmptyState icon={History} title="No resource activity yet" testid="dl-ledger-empty" />
        ) : (
          <div className="dl-panel rounded-lg divide-y divide-white/5" data-testid="dl-ledger">
            {ledger.slice().reverse().map((e) => (
              <div key={e.ledger_entry_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0"><span className="text-[10px] font-mono uppercase text-white/40">{e.transaction_type}</span><span className="text-sm text-white/70 ml-2">{e.reason}</span></div>
                <div className="flex items-center gap-3 shrink-0 font-mono text-sm">
                  <span className={Number(e.delta) < 0 ? "text-red-400" : "text-emerald-300"}>{Number(e.delta) > 0 ? "+" : ""}{e.delta} {e.resource_type === "cap_points" ? "CP" : "hrs"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Commit confirmation */}
      {confirm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div className="dl-panel-gold rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="dl-commit-modal">
            <div className="flex items-center justify-between mb-4"><Slogan>Commit Resource?</Slogan><button onClick={() => setConfirm(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button></div>
            <div className="font-display font-bold text-white text-xl">{confirm.name}</div>
            <div className="mt-3 space-y-1 text-sm font-mono">
              {confirm.cap_cost ? <div className="flex justify-between"><span className="text-white/50">Cost</span><span className="text-gold">{confirm.cap_cost} Cap Points</span></div> : null}
              {confirm.attorney_hour_cost ? <div className="flex justify-between"><span className="text-white/50">Cost</span><span className="text-gold">{confirm.attorney_hour_cost} Attorney Hours</span></div> : null}
              <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="text-white/50">Remaining after</span><span className={afterCap < 0 ? "text-red-400" : "text-white"}>{afterCap} CP · {afterHours} hrs</span></div>
            </div>
            <div className="mt-3 flex items-start gap-2 text-[11px] text-white/45"><AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" /> This is not reversible. Resources spent are not restored if the strategy fails.</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="dl-commit-reason"
              className="w-full mt-3 bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white text-sm focus:border-gold outline-none" />
            <div className="mt-4 flex gap-2 justify-end">
              <DLButton variant="ghost" onClick={() => setConfirm(null)}>Cancel</DLButton>
              <DLButton disabled={busy || cantAfford} onClick={commit} data-testid="dl-commit-confirm">{cantAfford ? "Can't afford" : "Commit Resource"}</DLButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
